import styled, { css } from 'styled-components';
import { ScFormGroup } from '@/components/form/groups/styled';
import { device, hexToRGB } from '@/styles/helpers';
import theme from '@/theme';

interface ScRegBonusProps {
  backgroundImage: string;
  termsOpen: boolean;
}

export const ScRegBonusTemplate = styled.div<ScRegBonusProps>`
  background:
    linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0) 70%,
      ${({ theme }) => theme.blackColor} 100%
    ),
    url(${({ backgroundImage }) => backgroundImage}) center / cover no-repeat;
  background-color: ${({ theme }) => hexToRGB(theme.blackColor, 0.4)};
  background-attachment: fixed;
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  justify-content: center;
  align-items: start;

  & .main__image {
    position: absolute;
    width: 100%;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: space-between;

    & img {
      position: relative;
      width: 464px;
      height: auto;
      max-width: 30%;
    }
  }

  & .main__hero {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    min-height: 100dvh;
    position: relative;
    z-index: 6;
    padding: 16px;

    & .logo-wrap {
      margin-bottom: 32px;
      @media (${device.mobile}) {
        margin-bottom: 16px;
      }
    }

    & .wrap-items {
      border-radius: 24px;
      display: flex;
      max-width: 960px;
      .terms-mob {
        display: none;
      }
      @media (${device.mobile}) {
        flex-direction: column;
        .terms-mob {
          display: flex;
          flex-direction: column;
          justify-content: center;
          margin: 0 auto;
          margin-top: 16px;
        }
      }
    }

    & .text-item {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      max-width: 50%;
      background: ${({ theme }) => hexToRGB(theme.blackColor, 0.6)};
      border-radius: 24px 0 0 24px;
      .terms-desk {
        display: flex;
        flex-direction: column;
        ${({ termsOpen }) =>
          termsOpen &&
          css`
            margin-top: 16px;
          `}
      }
      .terms-wrapper {
        display: flex;
        justify-content: center;
        padding: 0 40px;
      }
      @media (${device.mobile}) {
        max-width: 100%;
        border-radius: 24px 24px 0 0;
        .terms-desk {
          display: none;
        }
        .terms-wrapper {
          display: flex;
          justify-content: center;
          margin-top: 16px;
          padding: 0 16px;
        }
      }
    }

    & .characters-img {
      width: 480px;
      height: 238px;
      display: block;
      @media (${device.mobile}) {
        width: 240px;
        height: 119px;
      }
    }
    & .mob-img-container img {
      margin: 0 auto;
    }

    & .benefits {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-radius: 24px 0 0 0;
      gap: 24px;
      padding: 16px;
      background: ${({ theme }) => hexToRGB(theme.whiteColor, 0.2)};

      & .benefits__item {
        position: relative;
        padding-left: 36px;
        list-style-type: none;
        text-transform: uppercase;
        font-size: 14px;
        font-weight: 700;
        line-height: 20px;
        letter-spacing: 0.14px;
        text-align: center;
        max-width: calc(100% - 33%);
        width: 100%;

        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 28px;
          height: 28px;
          background-repeat: no-repeat;
          background-size: contain;
          background-position: center;
        }
      }
      & .benefits__item-0 {
        &::before {
          background-image: url('https://lu-img.s3.ap-southeast-2.amazonaws.com/lp/reg-rewards/benefits-icon-1.webp');
        }
      }
      & .benefits__item-1 {
        &::before {
          background-image: url('https://lu-img.s3.ap-southeast-2.amazonaws.com/lp/reg-rewards/benefits-icon-2.webp');
        }
      }
      & .benefits__item-2 {
        &::before {
          background-image: url('https://lu-img.s3.ap-southeast-2.amazonaws.com/lp/reg-rewards/benefits-icon-3.webp');
        }
      }
    }
    & .text-wrap {
      margin-inline: 16px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 8px;
      text-align: center;
      font-weight: 700;
      ${({ termsOpen }) =>
        termsOpen &&
        css`
          margin: 16px 16px 0;
        `}
      @media (${device.mobile}) {
        margin: 16px;
        gap: 8px;
      }

      & .title {
        color: ${theme.secondaryColor};
        text-transform: uppercase;
      }

      & .description {
        color: ${({ theme }) => theme.color2};
        font-size: 14px;
        line-height: 20px;
      }

      & .medium-text {
        font-size: 22px;
        line-height: 28px;
        letter-spacing: 0.44px;
        @media (${device.mobile}) {
          font-size: 18px;
          line-height: 24px;
          letter-spacing: 0.36px;
        }
      }
    }
    & .bonus,
    & .reg-title {
      font-size: 32px;
      line-height: 40px;
      letter-spacing: 0.96px;
      text-transform: uppercase;
      @media (${device.mobile}) {
        font-size: 28px;
        line-height: 36px;
        letter-spacing: 0.84px;
      }
    }

    @media (${device.mobile}) {
      & .reg-title {
        margin-bottom: 16px;
      }
    }

    & .reg-item {
      background-color: ${({ theme }) => theme.whiteColor};
      color: ${({ theme }) => theme.blackColor};
      padding: 32px;
      border-radius: 0 24px 24px 0;
      width: 100%;
      @media (${device.mobile}) {
        border-radius: 0 0 24px 24px;
        padding: 16px;
      }

      & .reg-banner-btn {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .reg-banner-btn button {
        height: 48px;
        .text {
          font-size: 16px;
        }
      }

      & > .reg-title {
        font-size: 32px;
        line-height: 40px;
        letter-spacing: 0.96px;
        text-transform: uppercase;
        text-align: center;
        margin-bottom: 32px;

        @media (${device.mobile}) {
          margin-bottom: 16px;
          font-size: 28px;
          line-height: 36px;
          letter-spacing: 0.84px;
        }
      }

      ${ScFormGroup}:has(.checkbox-list) {
        @media (${device.mobile}) {
          margin-top: 16px;
        }
      }

      ${ScFormGroup} {
        .content {
          .checkbox-list {
            margin-bottom: 24px;
            @media (${device.mobile}) {
              margin-bottom: 16px;
            }
          }
        }
      }

      & ${ScFormGroup}:last-child, ${ScFormGroup}:nth-last-child(2) {
        margin-bottom: 0;
      }

      & .checkbox-list {
        color: ${({ theme }) => theme.blackColor};
      }

      & .row + div {
        @media (${device.mobile}) {
          margin-block: 16px;
        }
      }

      & label div:first-child {
        width: 20px;
        height: 20px;
        min-width: 20px;
      }

      & .submit-box {
        & button {
          width: 100%;
          font-size: 16px;
        }
      }
    }
  }
  & .flying-img {
    zoom: 1;
  }
`;
